<template>
  <BaseModal
    :model-value="modelValue"
    @update:model-value="(v: boolean) => emit('update:modelValue', v)"
    title="Select Your Timezone"
    size="medium"
    :close-on-backdrop="false"
    :close-on-escape="false"
    :persistent="true"
    :before-dismiss="dirtyClose.beforeDismiss"
  >
    <div class="space-y-4">
      <BaseText size="medium" class="text-text">
        Please select your timezone to ensure dates and times are displayed correctly throughout the application.
      </BaseText>
      
      <div>
        <BaseText size="small" class="text-text-muted uppercase tracking-wide mb-2 block">
          Timezone
        </BaseText>
        <BaseSelect
          v-model="selectedTimezone"
          :options="timezoneOptions"
          placeholder="Select your timezone"
          :error="errors.timezone"
        />
        <BaseText size="small" class="text-text-muted mt-1">
          We've detected: {{ detectedTimezoneLabel }}
        </BaseText>
      </div>
    </div>
    
    <template #footer="{ requestClose }">
      <div class="flex items-center gap-3 justify-end">
        <BaseButton
          variant="secondary"
          @click="handleLater(requestClose)"
        >
          Later
        </BaseButton>
        <BaseButton
          variant="primary"
          @click="handleSave"
          :loading="saving"
          :disabled="!selectedTimezone"
        >
          Save Timezone
        </BaseButton>
      </div>
    </template>
  </BaseModal>

  <BaseConfirmDialog
    v-model="dirtyClose.showCloseConfirm"
    :title="dirtyClose.confirmDialogTitle"
    :message="dirtyClose.confirmDialogMessage"
    :confirm-label="dirtyClose.confirmDialogConfirmLabel"
    :cancel-label="dirtyClose.confirmDialogCancelLabel"
    :confirm-variant="dirtyClose.confirmDialogVariant"
    :layout="dirtyClose.confirmDialogLayout"
    @confirm="dirtyClose.onCloseConfirmConfirm"
    @cancel="dirtyClose.onCloseConfirmCancel"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import BaseConfirmDialog from '@/components/feedback/BaseConfirmDialog.vue'
import { useModalOpenBaselineGuard } from '@/composables/useModalOpenBaselineGuard'
import { useAuthStore } from '@/stores/auth'
import { toast } from '@/services/toastService'
import { getTimezoneOptions, getTimezoneLabel, detectUserTimezone } from '@/utils/timezoneUtils'
import BaseModal from '@/components/feedback/BaseModal.vue'
import BaseText from '@/components/ui/BaseText.vue'
import BaseSelect from '@/components/forms/BaseSelect.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'saved': []
}>()

const authStore = useAuthStore()

const selectedTimezone = ref<string>('')
const saving = ref(false)
const errors = ref<{ timezone?: string }>({})
const detectedTimezone = ref<string | null>(null)

const timezoneOptions = getTimezoneOptions()

const dirtyClose = useModalOpenBaselineGuard(
  () => props.modelValue,
  () => ({ selectedTimezone: selectedTimezone.value })
)

const detectedTimezoneLabel = computed(() => {
  if (!detectedTimezone.value) {
    return 'Unable to detect'
  }
  return getTimezoneLabel(detectedTimezone.value)
})

onMounted(() => {
  // Auto-detect user's timezone
  const detected = detectUserTimezone()
  detectedTimezone.value = detected
  
  if (detected) {
    selectedTimezone.value = detected
  } else {
    // Default to Eastern Time if detection fails
    selectedTimezone.value = 'America/New_York'
  }
})

const handleLater = (requestClose: () => void) => {
  // Store timestamp 1 hour from now
  const oneHourFromNow = Date.now() + (60 * 60 * 1000)
  sessionStorage.setItem('timezone_modal_later_until', oneHourFromNow.toString())
  requestClose()
}

const handleSave = async () => {
  // Clear previous errors
  errors.value = {}
  
  if (!selectedTimezone.value) {
    errors.value.timezone = 'Please select a timezone'
    return
  }

  saving.value = true
  
  try {
    await authStore.updateTimezone(selectedTimezone.value)
    
    // Clear the "Later" flag since timezone is now saved
    sessionStorage.removeItem('timezone_modal_later_until')
    
    toast.success('Timezone saved successfully')
    emit('saved')
    emit('update:modelValue', false)
  } catch (error: any) {
    console.error('Timezone save error:', error)
    
    if (error.response?.data?.errors) {
      Object.assign(errors.value, error.response.data.errors)
      toast.error('Please correct the form errors')
    } else if (error.response?.data?.message) {
      toast.error(error.response.data.message || 'Failed to save timezone')
    } else {
      toast.error(error.message || 'Failed to save timezone')
    }
  } finally {
    saving.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) {
      errors.value = {}
    }
  }
)
</script>

